import React, { useMemo } from 'react';
import PracticeCard from 'aquilla/core/organisms/Practice/Practice';
import ClassCard from 'aquilla/core/organisms/ClassCard';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import useSimilarAndOtherCourses from '@api/hooks/class/useSimilarAndOtherCourses';
import { getTopicGroup } from '@models/Course';
import useClassInfo from '@api/hooks/class/useClassInfo';
import useCourseInfo from '@api/hooks/course/useCourseInfo';
import useSimilarTopicPractice from '@api/hooks/class/useSimilarTopicPractice';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import Practice from '@icon/Practice';
import CourseCard from 'aquilla/core/organisms/CourseCard';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsFreeUser, getIsLoggedIn } from '@api/selectors/auth';
import {
  formatClassDetail,
  formatCourse,
  formatPractice,
  getVariant
} from './utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding-top: 40px;
  ${mediaQuery.uptoMobile} {
    gap: 20px;
    padding-top: 20px;
  }
`;

const PopularClassesContainer = styled.div`
  width: 1136px;
  margin: ${Spacings.SPACING_0B} auto ${Spacings.SPACING_0B};

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const StyledPracticeCard = styled(PracticeCard)`
  height: 100%;
`;

const SimilarCourses = () => {
  const {
    query: { uid: classUID }
  } = useRouter();
  const { classInfo } = useClassInfo(classUID);
  const { course: courseInfo } = useCourseInfo(classInfo.programme.uid);
  const { similarAndOtherCourses } = useSimilarAndOtherCourses(classUID);
  const { topicGroup } = useMemo(() => {
    return {
      topicGroup: getTopicGroup(courseInfo)
    };
  }, [courseInfo]);
  const goalUID = classInfo?.author?.levelInfo?.goal?.uid;
  const isFreeUser = useMyInfo(getIsFreeUser(goalUID));
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { similarTopicPractice } = useSimilarTopicPractice(topicGroup?.uid);
  const { goalInfo: goalData = {} } = useGoalInfo(topicGroup?.parent?.uid);
  const handleCardClick = (onClick) => () => {
    onClick();
  };

  return (
    <>
      <Container>
        {similarAndOtherCourses[0]?.data &&
          similarAndOtherCourses[0]?.data.length !== 0 && (
            <PopularClassesContainer key={similarAndOtherCourses.type}>
              <ShowCase
                title={similarAndOtherCourses[0]?.extraBlockInfo?.title}
                itemsPerSlide={4}
              >
                {similarAndOtherCourses[0]?.data?.map((courseData) => {
                  const { onClick, ...classCardProps } = formatClassDetail({
                    classInfo: courseData
                  });
                  return (
                    <ClassCard
                      key={courseData}
                      topicColor="#8873FF"
                      as="div"
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...classCardProps}
                      onClick={handleCardClick(onClick)}
                    />
                  );
                })}
              </ShowCase>
            </PopularClassesContainer>
          )}
        {similarAndOtherCourses[1]?.data &&
          similarAndOtherCourses[1]?.data.length !== 0 && (
            <PopularClassesContainer key={similarAndOtherCourses.type}>
              <ShowCase
                title={similarAndOtherCourses[1]?.extraBlockInfo?.title}
                itemsPerSlide={4}
                hideArrowButtons
              >
                {similarAndOtherCourses[1]?.data?.map((courseData) => {
                  const { onClick, ...classCardProps } = formatClassDetail({
                    classInfo: courseData
                  });
                  return (
                    <ClassCard
                      key={courseData}
                      topicColor="#8873FF"
                      as="div"
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...classCardProps}
                      onClick={handleCardClick(onClick)}
                    />
                  );
                })}
              </ShowCase>
            </PopularClassesContainer>
          )}
        {similarTopicPractice?.data &&
          similarTopicPractice?.data.length !== 0 && (
            <PopularClassesContainer key={similarTopicPractice?.type}>
              <ShowCase
                title={similarTopicPractice?.header || 'Similar topic practice'}
                itemsPerSlide={4}
              >
                {similarTopicPractice?.data?.map((practiceData) => (
                  <StyledPracticeCard
                    key={practiceData}
                    className="class-page-practice-card"
                    ProgressAdornment={<Practice />}
                    state="start"
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...formatPractice(practiceData, goalData)}
                  />
                ))}
              </ShowCase>
            </PopularClassesContainer>
          )}
        <PopularClassesContainer key={similarAndOtherCourses.type}>
          <ShowCase
            title={similarAndOtherCourses[2]?.extraBlockInfo?.title}
            itemsPerSlide={4}
            hideArrowButtons
          >
            {similarAndOtherCourses[2]?.data?.map((courseData) => (
              <CourseCard
                key={courseData}
                className="practice-card"
                variant={getVariant(isFreeUser, isLoggedIn, courseData?.isLive)}
                as="div"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...formatCourse(
                  courseData,
                  similarAndOtherCourses[2]?.extraBlockInfo?.title
                )}
              />
            ))}
          </ShowCase>
        </PopularClassesContainer>
      </Container>
    </>
  );
};

export default SimilarCourses;
